/**
 * @fileoverview Input/output schemas for the continuity-committee-v2 composite agent.
 *
 * This composite agent runs three specialized continuity checker agents in parallel
 * (chapter, character, item) to provide a complete continuity audit of a chapter.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  FindingSchema,
  WeaknessSchema,
  IssueSeverityEnum,
} from '@/ai/agents/issue-classification';

// Re-export for use by consumers
export { IssueSeverityEnum, FindingSchema, WeaknessSchema };

/**
 * Result from a single continuity checker agent.
 */
export const ContinuityResultSchema = z.object({
  agentId: z.string().describe('The ID of the continuity checker agent'),
  agentName: z.string().describe('Human-readable name of the continuity checker agent'),
  commentary: z.string().describe('Brief summary of findings and overall assessment'),
  rating: z.number().min(1).max(10).describe('Overall quality rating from 1-10'),
  findings: z.array(FindingSchema).describe('Structured list of issues found'),
  strengths: z.array(z.string()).describe('Things the draft does well'),
  weaknesses: z.array(WeaknessSchema).describe('Systemic issues requiring broader changes'),
  model: z.string().describe('The model used for this check'),
  durationMs: z.number().optional().describe('Time taken to run this check'),
});

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context for continuity agents.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  // Context fields used by all three continuity child agents.
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.previousChapters.schema.shape,
  // Full character snapshot (voice, physical description, clothing, knowledge,
  // emotional state, etc.) — the fields the continuity checkers verify against.
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  // POV context — bounds what counts as a "knowledge" contradiction.
  ...AGENT_SCHEMA_OBJECTS.pov.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  // Genre + per-genre continuity-evaluation lens. `genre` is auto-injected by
  // buildAgentContext from the project, `continuityGenreGuidance` is computed
  // by the caller via `getContinuityGenreGuidance()`.
  ...AGENT_SCHEMA_OBJECTS.genre.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.continuityGenreGuidance.schema.shape,
  // Prior-round author context for loop prevention. Forwarded to each child
  // checker so they can stabilise findings across re-runs.
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.acceptedFindings.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.rejectedFindings.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.acceptedWeaknesses.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.rejectedWeaknesses.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.currentStrengths.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output includes results from all three continuity checker agents.
 */
export const OutputSchema = z.object({
  checks: z.array(ContinuityResultSchema).describe(
    'Array of continuity check results from each specialized agent'
  ),
  successCount: z.number().describe(
    'Number of continuity agents that completed successfully'
  ),
  failedAgents: z.array(z.string()).optional().describe(
    'IDs of agents that failed, if any'
  ),
  totalDurationMs: z.number().describe(
    'Total time for parallel execution'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type ContinuityResult = z.infer<typeof ContinuityResultSchema>;
